/**
 * O aviso sonoro de mensagem nova.
 *
 * Sintetizado, não é arquivo: dois tons curtos pela Web Audio API. Um MP3 em
 * data URI engordaria o pacote e ainda poderia esbarrar na CSP do site que nos
 * embute — e isto aqui são vinte linhas.
 */

const CHAVE = "tecwb_chat_som"

/** Uma rajada do fluxo (boas-vindas + dica + menu) tem de virar UM som só. */
const INTERVALO_MINIMO_MS = 2500

let contexto: AudioContext | null = null
let ultimoToque = 0

export function somLigado(): boolean {
  try {
    return localStorage.getItem(CHAVE) !== "0"
  } catch {
    // Janela anônima, cookies bloqueados: o padrão continua valendo.
    return true
  }
}

export function definirSom(ligado: boolean): void {
  try {
    localStorage.setItem(CHAVE, ligado ? "1" : "0")
  } catch {
    // Sem onde guardar, a escolha vale só para esta visita. Melhor que quebrar.
  }
}

/**
 * Se o som deve tocar por esta mensagem.
 *
 * Pura de propósito — é a regra, e regra se testa. Em resumo: só o que vem da
 * empresa, nunca o eco do que a própria pessoa escreveu, nunca a saudação (que
 * não é novidade), e nada enquanto ela estiver olhando a conversa.
 */
export function devoTocar(params: {
  direcao: string
  ehSaudacao: boolean
  janelaAberta: boolean
  documentoVisivel: boolean
  quadroComFoco: boolean
}): boolean {
  // `inbound` é o que a PESSOA mandou (o widget desenha à direita); o que vem da
  // empresa é `outbound`. Trocar os dois faria o chat apitar para o próprio eco.
  if (params.direcao !== "outbound") return false
  if (params.ehSaudacao) return false

  const estaOlhando =
    params.janelaAberta && params.documentoVisivel && params.quadroComFoco
  return !estaOlhando
}

/**
 * Deixa o áudio pronto.
 *
 * O navegador só libera som depois de um gesto NESTE quadro, então isto é
 * chamado no primeiro clique (abrir o chat, mandar mensagem) — assim o contexto
 * já está de pé quando a resposta chegar, minutos depois, sem clique nenhum.
 */
export function prepararSom(): void {
  try {
    const Fabrica =
      window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!Fabrica) return
    if (!contexto) contexto = new Fabrica()
    if (contexto.state === "suspended") void contexto.resume()
  } catch {
    contexto = null
  }
}

/** Toca o aviso. Devolve se realmente tocou — o teste e o log agradecem. */
export function tocarNotificacao(agora: number = Date.now()): boolean {
  if (!somLigado()) return false
  if (agora - ultimoToque < INTERVALO_MINIMO_MS) return false

  prepararSom()
  if (!contexto || contexto.state !== "running") return false

  ultimoToque = agora
  const inicio = contexto.currentTime
  // Dois tons subindo (lá e mi), curtos. Volume baixo: é um aviso, não um alarme.
  const tons: Array<[number, number, number]> = [
    [880, 0, 0.12],
    [1318.5, 0.08, 0.16],
  ]

  for (const [frequencia, atraso, duracao] of tons) {
    const oscilador = contexto.createOscillator()
    const ganho = contexto.createGain()
    oscilador.type = "sine"
    oscilador.frequency.value = frequencia
    // Rampa exponencial não aceita zero, daí o 0.0001 nas pontas.
    ganho.gain.setValueAtTime(0.0001, inicio + atraso)
    ganho.gain.exponentialRampToValueAtTime(0.07, inicio + atraso + 0.012)
    ganho.gain.exponentialRampToValueAtTime(0.0001, inicio + atraso + duracao)
    oscilador.connect(ganho)
    ganho.connect(contexto.destination)
    oscilador.start(inicio + atraso)
    oscilador.stop(inicio + atraso + duracao + 0.02)
  }
  return true
}

/** Só para o teste: zera a trava de intervalo entre um caso e outro. */
export function reiniciarIntervaloParaTeste(): void {
  ultimoToque = 0
}
